import React from 'react';
import PropTypes from 'prop-types';
import { Box, styled } from '@mui/system';
import { alpha, useTheme } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';

// Check badge, top-left. Sits above the click layer and the selected ring.
const CheckBadge = styled(Box)({
  position: 'absolute',
  top: 8,
  left: 8,
  zIndex: 20,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.45))',
});

/**
 * Entity-agnostic card selection affordance. Drop it as the first child of any
 * `position: relative` card (item, list, author):
 *   - a coral ring around the whole card while `selected`
 *   - a full-card click layer that toggles selection while `selectionMode`
 *   - a check badge (filled when selected, empty ring otherwise)
 *
 * The parent card supplies the id and wires `onToggle` to
 * `toggleSelection(id)`; this component holds no entity knowledge.
 */
export default function SelectionOverlay(props) {
  const { selectionMode, selected, onToggle } = props;
  const coral = useTheme().palette.primary.main;

  if (!selectionMode && !selected) return null;

  const handleToggle = (e) => {
    e.stopPropagation();
    onToggle();
  };

  return (
    <>
      {selected && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            borderRadius: '8px',
            border: `2px solid ${coral}`,
            pointerEvents: 'none',
            zIndex: 15,
          }}
        />
      )}
      {selectionMode && (
        <Box
          onClick={handleToggle}
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 12,
            cursor: 'pointer',
            transition: 'background 0.12s',
            '&:hover': { background: alpha(coral, 0.06) },
          }}
        />
      )}
      {selectionMode && (
        <CheckBadge onClick={handleToggle} title="Select">
          {selected ? (
            <CheckCircleIcon sx={{ fontSize: 26, color: coral }} />
          ) : (
            <RadioButtonUncheckedIcon sx={{ fontSize: 26, color: '#ffffff' }} />
          )}
        </CheckBadge>
      )}
    </>
  );
}

SelectionOverlay.propTypes = {
  selectionMode: PropTypes.bool.isRequired,
  selected: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
};
